import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getActuatorNumberOfChannels, getActuatorState, getApplianceCapacityPerChannel, getConnectedApplianceIds, getUsedActuatorChannels, normalizeActuatorChannelConnections } from '../../Functions/ActuatorChannels';
import { getDetectorActuatorChannels, normalizeDetectorConnections } from '../../Functions/DetectorConnections';
import { ActuatorStateI, AppliancesStateI, AppStateI, DetectorStateI, DeviceSelectorCategory, DevicesStateI, DinDeviceI, HoveredItemI } from '../types/app';
import { EmailPasswordI } from '../types/auth';
import { DeviceI } from '../types/controllers';

const initAppState: AppStateI = {
  controllerType: '',
  hasUnsavedChanges: false,
  lastSavedConfigurationSignature: null,
  leftMenuShown: false,
  rightMenuShown: false,
  bottomBarOpen: false,
  selectedPage: '',
  isLoading: false,
  isEstablishingConnection: false,
  isConnected: false,
  selectingDevice: false,
  selectingThermostat: false,
  deviceSelectorCategory: 'controller',
  showApplianceDetail: false,
  showDeviceDetail: false,
  showActuatorDetail: false,
  showThermostatDetail: false,
  showDetectorDetail: false,
  showAddDevicePopup: false,
  pendingThermostatUsage: null,
  hoveredItem: null,
  showSaveDetail: false,
  configurationResetRevision: 0,
  totalDINModules: 16,
  floorplanImage: null,
  appliances: [] as AppliancesStateI[],
  devices: [] as DevicesStateI[],
  actuators: [] as ActuatorStateI[],
  detectors: [] as DetectorStateI[],
  otherDevices: [] as DinDeviceI[]
};

const getApplianceState = (actuator: number | undefined) => actuator !== undefined ? 'ok' : 'error';

const getSyncedApplianceState = (
  appliance: AppliancesStateI,
  controllers: DeviceI[],
  actuators: ActuatorStateI[]
) => {
  if (appliance.actuator !== undefined || appliance.applianceType !== 'hvac') {
    return getApplianceState(appliance.actuator);
  }

  const directlyConnectedThermostat = controllers.some((controller) =>
    controller.type === 'thermostat'
    && (controller.thermostatRequiredActuator === null || controller.thermostatRequiredActuator === undefined)
    && controller.iconsData.some((slot) => (slot.applianceId ?? []).includes(appliance.id))
  );

  if (directlyConnectedThermostat) {
    return 'ok' as const;
  }

  const requiredActuatorConnected = controllers.some((controller) => {
    const requiredActuator = controller.thermostatRequiredActuator;
    const requiredActuatorTypes = typeof requiredActuator === 'string'
      ? [requiredActuator]
      : Array.isArray(requiredActuator)
        ? requiredActuator
        : requiredActuator
          ? [requiredActuator.type]
          : [];
    const connectedActuatorIds = controller.connectedActuatorIds
      ?? (controller.connectedActuator === undefined ? [] : [controller.connectedActuator]);
    if (controller.type !== 'thermostat' || requiredActuatorTypes.length === 0 || connectedActuatorIds.length === 0) {
      return false;
    }

    return connectedActuatorIds.some((actuatorId) => {
      const connectedActuator = actuators.find((actuator) => actuator.id === actuatorId);
      return appliance.actuator === actuatorId && requiredActuatorTypes.includes(connectedActuator?.type ?? '');
    });
  });

  return requiredActuatorConnected ? 'warning' as const : 'error' as const;
};

const getControllerLinkedActuatorIds = (controllers: DeviceI[]) => {
  const linkedActuatorIds = new Set<number>();

  controllers.forEach((controller) => {
    controller.iconsData.forEach((slot) => {
      slot.connectedActuatorChannels?.forEach((link) => linkedActuatorIds.add(link.actuatorId));
      if (slot.connectedActuatorId !== undefined) {
        linkedActuatorIds.add(slot.connectedActuatorId);
      }
    });
  });

  return linkedActuatorIds;
};

const getControllerLinkedActuatorChannels = (controllers: DeviceI[]) => {
  const linkedChannelsByActuatorId = new Map<number, Set<number>>();

  const addLink = (actuatorId: number, channel: number) => {
    const channels = linkedChannelsByActuatorId.get(actuatorId) ?? new Set<number>();
    channels.add(channel);
    linkedChannelsByActuatorId.set(actuatorId, channels);
  };

  controllers.forEach((controller) => {
    controller.iconsData.forEach((slot) => {
      slot.connectedActuatorChannels?.forEach((link) => addLink(link.actuatorId, link.channel));
      if (slot.connectedActuatorId !== undefined && slot.connectedActuatorChannel !== undefined) {
        addLink(slot.connectedActuatorId, slot.connectedActuatorChannel);
      }
    });
  });

  return linkedChannelsByActuatorId;
};

const getActuatorStatus = (
  actuator: ActuatorStateI,
  connectedAppliances: number[],
  controllers: DeviceI[],
  detectors: DetectorStateI[]
) => {
  const hasRequiredThermostatConnection = controllers.some((controller) =>
    controller.type === 'thermostat'
    && controller.thermostatRequiredActuator !== null
    && controller.thermostatRequiredActuator !== undefined
    && (controller.connectedActuatorIds ?? [controller.connectedActuator]).includes(actuator.id)
  );
  // Legacy/simple actuators (built-ins, direct links) are wired as a whole, not per channel.
  const isWholeActuatorControllerConnected = actuator.linkedToController === true
    || actuator.builtIn === true
    || hasRequiredThermostatConnection;
  const numberOfChannels = getActuatorNumberOfChannels(actuator);
  const controllerLinkedChannels = getControllerLinkedActuatorChannels(controllers).get(actuator.id ?? -1) ?? new Set<number>();
  detectors.forEach((detector) => {
    getDetectorActuatorChannels(detector, actuator).forEach((channel) => controllerLinkedChannels.add(channel));
  });
  const hasAllChannelsControllerConnected = isWholeActuatorControllerConnected
    || controllerLinkedChannels.size >= numberOfChannels;
  const hasAnyControllerConnection = isWholeActuatorControllerConnected
    || controllerLinkedChannels.size > 0;

  const usedActuatorChannels = getUsedActuatorChannels(actuator);
  const hasAllApplianceChannelsUsed = usedActuatorChannels >= numberOfChannels;
  const hasAnyApplianceConnected = usedActuatorChannels > 0;

  return getActuatorState(
    hasAllApplianceChannelsUsed,
    hasAllChannelsControllerConnected,
    hasAnyApplianceConnected,
    hasAnyControllerConnection
  );
};

const appSlice = createSlice({
  name: 'app',
  initialState: initAppState,
  reducers: {
    setControllerType: (state, action: PayloadAction<string>) => {
      state.controllerType = action.payload;
    },
    setHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
    },
    setSavedConfigurationSignature: (state, action: PayloadAction<string>) => {
      state.lastSavedConfigurationSignature = action.payload;
      state.hasUnsavedChanges = false;
    },
    resetUnsavedChangesTracking: (state) => {
      state.lastSavedConfigurationSignature = null;
      state.hasUnsavedChanges = false;
    },
    toggleLeftMenu: (state) => {
      state.leftMenuShown = !state.leftMenuShown;
    },
    setLeftMenuShown: (state, action: PayloadAction<boolean>) => {
      state.leftMenuShown = action.payload;
    },
    toggleRightMenu: (state) => {
      state.rightMenuShown = !state.rightMenuShown;
    },
    setBottomBarOpen: (state, action: PayloadAction<boolean>) => {
      state.bottomBarOpen = action.payload;
    },
    setSelectedTab: (state, action: PayloadAction<string>) => {
      state.selectedPage = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    // eslint-disable-next-line 
    startConnecting: (state, action: PayloadAction<EmailPasswordI>) => {
      // action is present because of passing arguments to wssMiddleware 
      state.isEstablishingConnection = true;
    },
    connectionEstablished: (state) => {
      state.isConnected = true;
      state.isEstablishingConnection = true;
    },
    setAppliances: (state, action: PayloadAction<AppliancesStateI[]>) => {
      state.appliances = action.payload.map((incoming) => {
        const existing = state.appliances.find((a) => a.id === incoming.id);
        const connectedTo = incoming.connectedTo ?? existing?.connectedTo ?? [];
        const actuator = 'actuator' in incoming ? incoming.actuator : (existing?.actuator ?? undefined);
        const x = incoming.x ?? existing?.x;
        const y = incoming.y ?? existing?.y;
        // hvac appliances can be 'ok' via a direct thermostat link with no actuator;
        // keep that state instead of recomputing it from the actuator alone
        const applianceState = incoming.applianceType === 'hvac'
          ? (existing?.state ?? getApplianceState(actuator))
          : getApplianceState(actuator);
        return {
          ...incoming,
          connectedTo,
          actuator,
          x,
          y,
          state: applianceState
        };
      });
    },
    addAppliance: (state, action: PayloadAction<AppliancesStateI>) => {
      state.appliances.push(action.payload);
    },
    setActuators: (state, action: PayloadAction<ActuatorStateI[]>) => {
      state.actuators = action.payload.map((incoming) => {
        const existing = state.actuators.find((a) => a.id === incoming.id);
        const numberOfChannels = getActuatorNumberOfChannels({
          ...existing,
          ...incoming
        });
        const connectedAppliancesByChannel = normalizeActuatorChannelConnections(
          incoming.connectedAppliancesByChannel ?? existing?.connectedAppliancesByChannel,
          numberOfChannels,
          incoming.connectedAppliances ?? existing?.connectedAppliances ?? [],
          getApplianceCapacityPerChannel({ ...existing, ...incoming })
        );
        const connectedAppliances = getConnectedApplianceIds({
          ...existing,
          ...incoming,
          numberOfChannels,
          connectedAppliancesByChannel
        });
        const x = incoming.x ?? existing?.x;
        const y = incoming.y ?? existing?.y;
        const maximumApplianceConnections = incoming.maximumApplianceConnections
          ?? existing?.maximumApplianceConnections
          ?? numberOfChannels;

        return {
          ...incoming,
          connectedAppliances,
          connectedAppliancesByChannel,
          x,
          y,
          numberOfChannels,
          maximumApplianceConnections,
          linkedToController: incoming.linkedToController ?? existing?.linkedToController ?? incoming.builtIn === true,
          state: getActuatorStatus(
            {
              ...incoming,
              builtIn: incoming.builtIn ?? existing?.builtIn,
              numberOfChannels,
              connectedAppliances,
              connectedAppliancesByChannel,
              linkedToController: incoming.linkedToController
                ?? existing?.linkedToController
                ?? incoming.builtIn === true
            },
            connectedAppliances,
            [],
            state.detectors
          )
        };
      });
    },
    addActuator: (state, action: PayloadAction<ActuatorStateI>) => {
      state.actuators.push(action.payload);
    },
    setActuatorPosition: (state, action: PayloadAction<{ id: number; x: number; y: number }>) => {
      const actuator = state.actuators.find((item) => item.id === action.payload.id);
      if (actuator) {
        actuator.x = action.payload.x;
        actuator.y = action.payload.y;
      }
    },
    setActuatorControllerLinks: (state, action: PayloadAction<DeviceI[]>) => {
      state.actuators = state.actuators.map((actuator) => ({
        ...actuator,
        linkedToController: actuator.builtIn === true
          || getControllerLinkedActuatorIds(action.payload).has(actuator.id ?? -1)
          || action.payload.some((controller) =>
            controller.type === 'thermostat'
            && controller.thermostatRequiredActuator !== null
            && controller.thermostatRequiredActuator !== undefined
            && (controller.connectedActuatorIds ?? [controller.connectedActuator]).includes(actuator.id)
          ),
        state: getActuatorStatus(
          {
            ...actuator,
            linkedToController: actuator.builtIn === true
              || getControllerLinkedActuatorIds(action.payload).has(actuator.id ?? -1)
              || action.payload.some((controller) =>
                controller.type === 'thermostat'
                && controller.thermostatRequiredActuator !== null
                && controller.thermostatRequiredActuator !== undefined
                && (controller.connectedActuatorIds ?? [controller.connectedActuator]).includes(actuator.id)
              )
          },
          getConnectedApplianceIds(actuator),
          [],
          state.detectors
        )
      }));
    },
    setDetectors: (state, action: PayloadAction<DetectorStateI[]>) => {
      state.detectors = action.payload.map((incoming) => normalizeDetectorConnections(incoming, state.actuators));

      state.actuators = state.actuators.map((actuator) => ({
        ...actuator,
        state: getActuatorStatus(
          actuator,
          getConnectedApplianceIds(actuator),
          [],
          state.detectors
        )
      }));
    },
    addDetector: (state, action: PayloadAction<DetectorStateI>) => {
      state.detectors.push(action.payload);
    },
    setOtherDevices: (state, action: PayloadAction<DinDeviceI[]>) => {
      state.otherDevices = action.payload;
    },
    connectApplianceToController: (state, action: PayloadAction<{ applianceId: number, controllerId: number }>) => {
      const appliance = state.appliances.find((a) => a.id === action.payload.applianceId);
      if(!appliance){
        return;
      }

      const current = appliance.connectedTo ?? [];
      if(!current.includes(action.payload.controllerId)){
        appliance.connectedTo = [...current, action.payload.controllerId];
      }else{
        appliance.connectedTo = current;
      }

      appliance.state = getApplianceState(appliance.actuator ?? undefined);
    },
    disconnectApplianceFromController: (state, action: PayloadAction<{ applianceId: number, controllerId: number }>) => {
      const appliance = state.appliances.find((a) => a.id === action.payload.applianceId);
      if(!appliance){
        return;
      }

      const current = appliance.connectedTo ?? [];
      appliance.connectedTo = current.filter((id) => id !== action.payload.controllerId);
      appliance.state = getApplianceState(appliance.actuator ?? undefined);
    },
    disconnectControllerFromAllAppliances: (state, action: PayloadAction<number>) => {
      const controllerId = action.payload;
      state.appliances.forEach((appliance) => {
        const current = appliance.connectedTo ?? [];
        const next = current.filter((id) => id !== controllerId);
        appliance.connectedTo = next;
        appliance.state = getApplianceState(appliance.actuator ?? undefined);
      });
    },
    syncApplianceControllerConnections: (state, action: PayloadAction<DeviceI[]>) => {
      const controllers = action.payload;
      const applianceConnectionsByController = new Map<number, Set<number>>();

      controllers.forEach((controller) => {
        controller.iconsData.forEach((slot) => {
          (slot.applianceId ?? []).forEach((applianceId) => {
            const existing = applianceConnectionsByController.get(applianceId);
            if (existing) {
              existing.add(controller.id);
            } else {
              applianceConnectionsByController.set(applianceId, new Set([controller.id]));
            }
          });
        });
      });

      state.appliances = state.appliances.map((appliance) => {
        const nextConnectedTo = Array.from(applianceConnectionsByController.get(appliance.id) ?? []);
        return {
          ...appliance,
          connectedTo: nextConnectedTo,
          state: getSyncedApplianceState(
            { ...appliance, connectedTo: nextConnectedTo },
            controllers,
            state.actuators
          )
        };
      });
    },
    setSelectingDevice: (state, action: PayloadAction<boolean>) => {
      state.selectingDevice = action.payload;
      if (!action.payload) {
        state.selectingThermostat = false;
        state.deviceSelectorCategory = 'controller';
      }
    },
    setSelectingThermostat: (state, action: PayloadAction<boolean>) => {
      state.selectingThermostat = action.payload;
      state.deviceSelectorCategory = action.payload ? 'thermostat' : 'controller';
    },
    setDeviceSelectorCategory: (state, action: PayloadAction<DeviceSelectorCategory>) => {
      state.deviceSelectorCategory = action.payload;
      state.selectingThermostat = action.payload === 'thermostat';
    },
    setShowApplianceDetail: (state, action: PayloadAction<boolean>) => {
      state.showApplianceDetail = action.payload;
    },
    setShowDeviceDetail: (state, action: PayloadAction<boolean>) => {
      state.showDeviceDetail = action.payload;
    },
    setShowActuatorDetail: (state, action: PayloadAction<boolean>) => {
      state.showActuatorDetail = action.payload;
      if (action.payload) {
        state.actuators = state.actuators.map((actuator) => ({
          ...actuator,
          state: getActuatorStatus(
            actuator,
            getConnectedApplianceIds(actuator),
            [],
            state.detectors
          )
        }));
      }
    },
    setShowSaveDetail: (state, action: PayloadAction<boolean>) => {
      state.showSaveDetail = action.payload;
    },
    setShowThermostatDetail: (state, action: PayloadAction<boolean>) => {
      state.showThermostatDetail = action.payload;
    },
    setShowDetectorDetail: (state, action: PayloadAction<boolean>) => {
      state.showDetectorDetail = action.payload;
      if (action.payload) {
        state.actuators = state.actuators.map((actuator) => ({
          ...actuator,
          state: getActuatorStatus(
            actuator,
            getConnectedApplianceIds(actuator),
            [],
            state.detectors
          )
        }));
      }
    },
    setHoveredItem: (state, action: PayloadAction<HoveredItemI | null>) => {
      state.hoveredItem = action.payload;
    },
    setApplianceLabel: (state, action: PayloadAction<{ id: number; label: string }>) => {
      const appliance = state.appliances.find((a) => a.id === action.payload.id);
      if (appliance) {
        appliance.label = action.payload.label;
      }
    },
    resetConfiguration: (state) => {
      state.appliances = [] as AppliancesStateI[];
      state.devices = [] as DevicesStateI[];
      state.actuators = [] as ActuatorStateI[];
      state.detectors = [] as DetectorStateI[];
      state.otherDevices = [] as DinDeviceI[];
      state.hoveredItem = null;
      state.showDeviceDetail = false;
      state.showSaveDetail = false;
      state.selectingDevice = false;
      state.selectingThermostat = false;
      state.deviceSelectorCategory = 'controller';
      state.configurationResetRevision += 1;
      state.lastSavedConfigurationSignature = null;
      state.hasUnsavedChanges = false;
    },
    setShowAddDevicePopup: (state, action: PayloadAction<boolean>) => {
      state.showAddDevicePopup = action.payload;
    },
    setPendingThermostatUsage: (state, action: PayloadAction<string | null>) => {
      state.pendingThermostatUsage = action.payload;
    },
    setFloorplanImage: (state, action: PayloadAction<string | null>) => {
      state.floorplanImage = action.payload;
    },
    setTotalDINModules: (state, action: PayloadAction<number>) => {
      state.totalDINModules = action.payload;
    },
  }
});

export const {
  setControllerType,
  setHasUnsavedChanges,
  setSavedConfigurationSignature,
  resetUnsavedChangesTracking,
  toggleLeftMenu,
  setLeftMenuShown,
  toggleRightMenu,
  setBottomBarOpen,
  setSelectedTab,
  setIsLoading,
  startConnecting,
  connectionEstablished,
  setAppliances,
  addAppliance,
  setActuators,
  addActuator,
  setActuatorPosition,
  setActuatorControllerLinks,
  setDetectors,
  addDetector,
  setOtherDevices,
  connectApplianceToController,
  disconnectApplianceFromController,
  disconnectControllerFromAllAppliances,
  syncApplianceControllerConnections,
  setSelectingDevice,
  setSelectingThermostat,
  setDeviceSelectorCategory,
  setShowApplianceDetail,
  setShowDeviceDetail,
  setShowActuatorDetail,
  setShowSaveDetail,
  setShowThermostatDetail,
  setShowDetectorDetail,
  setHoveredItem,
  setApplianceLabel,
  resetConfiguration,
  setShowAddDevicePopup,
  setPendingThermostatUsage,
  setFloorplanImage,
  setTotalDINModules
} = appSlice.actions;

export default appSlice.reducer;
